import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { ChevronLeft, Globe } from "lucide-react";
import { SpamTabContent } from "./tabs/spam-tab";
import { IpTabContent } from "./tabs/ip-tab";
import { DomainTabContent } from "./tabs/domain-tab";
import { AuthTabContent } from "./tabs/auth-tab";
import { EncryptionTabContent } from "./tabs/encryption-tab";
import { DeliveryErrorTabContent } from "./tabs/delivery-error-tab";
import { useState } from "react";
import { FeedbackLoopTabContent } from "./tabs/feedback-loop-tab";
import { ComplianceTabContent } from "./tabs/compliance-tab";

const tabOptions = [
  { value: "compliance", label: "Compliance" },
  { value: "spam", label: "Spam rate" },
  { value: "feedback-loop", label: "Feedback loop" },
  { value: "auth", label: "Authentication" },
  { value: "encryption", label: "Encryption" },
  // { value: "ip", label: "IP reputation" },
  // { value: "domain", label: "Domain reputation" },
  { value: "delivery-error", label: "Delivery error" },
];

function RouteComponent() {
  const [date, setDate] = useState<string>("last7days");
  const [activeTab, setActiveTab] = useState<string>("compliance");
  const domain = useParams({
    from: "/_private/postmaster/$postmaster",
  }).postmaster;

  return (
    <div className="w-full">
      <div className="space-y-4">
        {/* Header with domain and controls */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="..">
              <Button variant="ghost" size="icon">
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div className="flex items-center gap-2 rounded-lg bg-white px-4 py-2 shadow-sm dark:bg-secondary">
              <Globe className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium break-all">{domain}</span>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full sm:w-auto">
            <Badge
              variant="secondary"
              className="bg-green-100 text-green-700 hover:bg-green-100 hover:text-green-700 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/20 whitespace-nowrap"
            >
              System Connected
            </Badge>
            <Select value={date} onValueChange={(value) => setDate(value)}>
              <SelectTrigger className="w-full sm:w-[140px] bg-background">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last7days">Last 7 days</SelectItem>
                <SelectItem value="last30days">Last 30 days</SelectItem>
                <SelectItem value="last60days">Last 60 days</SelectItem>
                <SelectItem value="last90days">Last 90 days</SelectItem>
                {/* <SelectItem value="last120days">Last 120 days</SelectItem> */}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Mobile Tab Navigation */}
        <div className="md:hidden">
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="w-full bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {tabOptions.map((tab) => (
                <SelectItem key={tab.value} value={tab.value}>
                  {tab.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Desktop Tabs and content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          {/* Desktop Tab list */}
          <TabsList className="hidden md:flex border">
            {tabOptions.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="flex-1 text-xs lg:text-sm px-2 lg:px-4"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Tab content */}
          <div className="mt-4">
          <TabsContent value="compliance" className="m-0">
              <ComplianceTabContent date={date} domain={domain} />
            </TabsContent>
            <TabsContent value="spam" className="m-0">
              <SpamTabContent date={date} domain={domain} />
            </TabsContent>
            <TabsContent value="ip" className="m-0">
              <IpTabContent date={date} domain={domain} />
            </TabsContent>
            <TabsContent value="domain" className="m-0">
              <DomainTabContent date={date} domain={domain} />
            </TabsContent>
            <TabsContent value="auth" className="m-0">
              <AuthTabContent date={date} domain={domain} />
            </TabsContent>
            <TabsContent value="encryption" className="m-0">
              <EncryptionTabContent date={date} domain={domain} />
            </TabsContent>
            <TabsContent value="delivery-error" className="m-0">
              <DeliveryErrorTabContent date={date} domain={domain} />
            </TabsContent>
            <TabsContent value="feedback-loop" className="m-0">
              <FeedbackLoopTabContent date={date} domain={domain} />
            </TabsContent>
            
          </div>
        </Tabs>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/_private/postmaster/$postmaster")({
  component: RouteComponent,
});
